// client/src/routes/brand.tsx — the brand check page (doc 05 UI-002 criterion 2; D-313).
//
// This route is the visual gate for the theme port. It renders the DaisyUI components the
// criterion names — btn, badge, stat, card, modal, table table-zebra — plus the classes
// index.css adds on its own (chip-*, section-card, and the DaisyUI 4->5 form-control shim),
// so that "the theme is correct" is something a reviewer can SEE rather than infer from CSS.
//
// It is also the §3.5 M0 sweep target: every control below is written in post-sweep form
// (v4 utility names per §3.4, DaisyUI 5 class names and native <dialog> per §3.5), so a
// straggler from the brand guide shows up here first.
//
// THE RULE THIS PAGE ITSELF OBEYS (doc 05 UI-002 criterion 4): not one colour is set outside
// the DaisyUI/@theme tokens. Every hex printed below is printed as TEXT — a label telling the
// reviewer what to expect — never as a style. Sampling a swatch must yield the hex beside it;
// if it does not, index.css drifted from doc 04 §3.2.
//
// AND THE RULE I18N-001 ADDS: not one user-visible string is written here. Copy lives in
// romanian.json under `dev.brand.*`, resolved through LITERAL t() keys, and paragraphs that
// contain <code>/<kbd> use <Trans> rather than being split around the tag (§9.6). Two
// categories are deliberately NOT keys — see sampleRows (D-321) and the token captions
// (D-322).
//
// UI-003 replaces the bare <main> wrapper with the app shell. That does not change what the
// page is for.

// ── DEVELOPMENT ONLY (D-2634, amending D-314) ─────────────────────────────────────────────
// A production build answers this path with SCR-SYS-404, exactly as it answers a path that was
// never a route, and carries none of this page's code. `beforeLoad` throws `notFound()` before
// anything renders, and `import.meta.env.DEV` — replaced by the literal `false` when Vite builds
// for production — leaves the component nothing to point at, so Rollup drops it and everything
// only it uses. `vite` (the dev server) and Vitest both run in development mode and keep the page.

import { useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';

export const Route = createFileRoute('/brand')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  component: import.meta.env.DEV ? BrandComponent : undefined,
});

/**
 * The inline tags allowed inside a translated paragraph. Keeping the map here — rather than
 * splitting a sentence around a <code> in JSX — is what lets a translator move the tag to
 * wherever their grammar needs it (§9.6; Appendix B).
 */
const RICH = {
  code: <code className="font-mono" />,
  kbd: <kbd className="kbd kbd-sm" />,
} as const;

const SEMANTIC_SWATCHES = [
  { token: 'success', className: 'bg-success text-success-content', hex: '#22c55e' },
  { token: 'warning', className: 'bg-warning text-warning-content', hex: '#f59e0b' },
  { token: 'error', className: 'bg-error text-error-content', hex: '#ef4444' },
  { token: 'info', className: 'bg-info text-info-content', hex: '#3b82f6' },
] as const;

/** DaisyUI badge tokens, rendered under their own names — identifiers, not copy (D-322). */
const BADGE_TOKENS = [
  { token: 'primary', className: 'badge badge-primary' },
  { token: 'secondary', className: 'badge badge-secondary' },
  { token: 'accent', className: 'badge badge-accent' },
  { token: 'success', className: 'badge badge-success' },
  { token: 'warning', className: 'badge badge-warning' },
  { token: 'error', className: 'badge badge-error' },
  { token: 'info', className: 'badge badge-info' },
] as const;

const SAMPLE_CARD = { apartment: 'Ap. 12, Str. Aviatorilor', until: '31.12.2026', rent: '2.400 RON' } as const;

const SAMPLE_STATS = { collected: '18.400 RON', overdue: '3.150 RON', profit: '7.920 RON' } as const;

function BrandComponent() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();

  // The copy-bearing tables live INSIDE the component so that every key is a literal argument
  // to t(). A key reached through a variable (`t(row.statusKey)`) is invisible to
  // i18next-parser, so it would extract as a missing key and its resource entry as an orphan —
  // `npm run i18n:check` fails both, and `enums.*` is the only computed key the app sanctions
  // (§9.6.3). Rebuilding four short arrays per render is not a cost worth a hole in the gate.

  /** Swatches are described by TOKEN; the hex is the caption, never the style (D-322). */
  const brandSwatches = [
    { token: 'primary', className: 'bg-primary text-primary-content', hex: '#FF005E', role: t('dev.brand.swatches.primary') },
    { token: 'secondary', className: 'bg-secondary text-secondary-content', hex: '#FF1E8D', role: t('dev.brand.swatches.secondary') },
    { token: 'accent', className: 'bg-accent text-accent-content', hex: '#FF1E8D', role: t('dev.brand.swatches.accent') },
    { token: 'neutral', className: 'bg-neutral text-neutral-content', hex: '#1f2937', role: t('dev.brand.swatches.neutral') },
  ];

  const surfaceSwatches = [
    { token: 'base-100', className: 'bg-base-100', hex: '#ffffff', role: t('dev.brand.surfaces.base100') },
    { token: 'base-200', className: 'bg-base-200', hex: '#f9fafb', role: t('dev.brand.surfaces.base200') },
    { token: 'base-300', className: 'bg-base-300', hex: '#e5e7eb', role: t('dev.brand.surfaces.base300') },
  ];

  /** The §3.2 chip pairs — the accessible alternative to solid badge fills (§5.2, §10.2). */
  const chips = [
    { id: 'paid', className: 'chip chip-success', label: t('dev.brand.chips.paid'), ratio: '6.5:1' },
    { id: 'due', className: 'chip chip-warning', label: t('dev.brand.chips.due'), ratio: '6.4:1' },
    { id: 'overdue', className: 'chip chip-error', label: t('dev.brand.chips.overdue'), ratio: '6.8:1' },
    { id: 'processing', className: 'chip chip-info', label: t('dev.brand.chips.processing'), ratio: '7.1:1' },
    { id: 'draft', className: 'chip chip-neutral', label: t('dev.brand.chips.draft'), ratio: '9.4:1' },
    { id: 'highlighted', className: 'chip chip-primary', label: t('dev.brand.chips.highlighted'), ratio: '5.8:1' },
  ];

  /**
   * Sample rows exist to make table-zebra's alternating bands visible, and they draw the line
   * this whole item is about (D-321): the apartment, the tenant and the amount are DATA — on a
   * real screen they arrive from the API and are never translated — while the column headers
   * and the status chip are COPY. Only the second kind is a key.
   */
  const sampleRows = [
    { apartment: 'Ap. 12, Str. Aviatorilor', tenant: 'Popescu Ion', rent: '2.400 RON', chip: 'chip chip-success', status: t('dev.brand.chips.paid') },
    { apartment: 'Ap. 3, Bd. Unirii', tenant: 'SC Delta Prest SRL', rent: '3.150 RON', chip: 'chip chip-warning', status: t('dev.brand.chips.due') },
    { apartment: 'Ap. 7, Str. Dorobanți', tenant: 'Ionescu Maria', rent: '1.900 RON', chip: 'chip chip-error', status: t('dev.brand.chips.overdue') },
    { apartment: 'Ap. 21, Calea Victoriei', tenant: 'Georgescu Andrei', rent: '2.750 RON', chip: 'chip chip-neutral', status: t('dev.brand.chips.draft') },
  ];

  return (
    <main className="min-h-screen bg-base-200 p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <header>
          <h1 className="text-3xl font-bold text-base-content">{t('dev.brand.title')}</h1>
          <p className="mt-2 text-base-content/70">
            <Trans i18nKey="dev.brand.lead" components={RICH} />
          </p>
        </header>

        {/* ---------------------------------------------------------------- Tokens */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.tokens.title')}</h2>
          <p className="mt-1 text-sm text-base-content/70">{t('dev.brand.tokens.lead')}</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {brandSwatches.map((s) => (
              <div key={s.token} className={`rounded-box p-4 ${s.className}`}>
                <div className="font-semibold">{s.token}</div>
                <div className="font-mono text-sm">{s.hex}</div>
                <div className="mt-1 text-xs opacity-90">{s.role}</div>
              </div>
            ))}
          </div>

          <h3 className="mt-6 text-lg font-semibold text-base-content">
            {t('dev.brand.tokens.semanticTitle')}
          </h3>
          <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SEMANTIC_SWATCHES.map((s) => (
              <div key={s.token} className={`rounded-box p-4 ${s.className}`}>
                <div className="font-semibold">{s.token}</div>
                <div className="font-mono text-sm">{s.hex}</div>
              </div>
            ))}
          </div>

          <h3 className="mt-6 text-lg font-semibold text-base-content">
            {t('dev.brand.tokens.surfaceTitle')}
          </h3>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {surfaceSwatches.map((s) => (
              <div
                key={s.token}
                className={`rounded-box border border-base-300 p-4 text-base-content ${s.className}`}
              >
                <div className="font-semibold">{s.token}</div>
                <div className="font-mono text-sm">{s.hex}</div>
                <div className="mt-1 text-xs opacity-70">{s.role}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- btn */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.buttons.title')}</h2>
          <p className="mt-1 text-sm text-base-content/70">
            <Trans i18nKey="dev.brand.buttons.lead" components={RICH} />
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary">{t('common.save')}</button>
            <button type="button" className="btn btn-secondary">{t('dev.brand.buttons.submit')}</button>
            <button type="button" className="btn btn-accent">{t('dev.brand.buttons.highlight')}</button>
            <button type="button" className="btn btn-outline btn-primary">{t('common.cancel')}</button>
            <button type="button" className="btn btn-ghost">{t('common.close')}</button>
            <button type="button" className="btn btn-primary btn-sm">{t('dev.brand.buttons.small')}</button>
            <button type="button" className="btn btn-primary" disabled>
              {t('dev.brand.buttons.disabled')}
            </button>
          </div>
          <p className="mt-3 text-xs text-base-content/60">
            <Trans i18nKey="dev.brand.buttons.ghostNote" components={RICH} />
          </p>
        </section>

        {/* ---------------------------------------------------------------- badge + chip */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.badges.title')}</h2>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {BADGE_TOKENS.map((b) => (
              <span key={b.token} className={b.className}>
                {b.token}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm text-base-content/70">{t('dev.brand.badges.lead')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {chips.map((c) => (
              <div key={c.id} className="flex flex-col items-center gap-1">
                <span className={c.className}>{c.label}</span>
                <span className="font-mono text-xs text-base-content/60">{c.ratio}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- stat */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.stats.title')}</h2>
          <div className="mt-4 stats stats-vertical w-full border border-base-300 lg:stats-horizontal">
            <div className="stat">
              <div className="stat-title">{t('dev.brand.stats.collectedTitle')}</div>
              <div className="stat-value text-primary">{SAMPLE_STATS.collected}</div>
              <div className="stat-desc">{t('dev.brand.stats.collectedDesc')}</div>
            </div>
            <div className="stat">
              <div className="stat-title">{t('dev.brand.stats.overdueTitle')}</div>
              <div className="stat-value text-error">{SAMPLE_STATS.overdue}</div>
              <div className="stat-desc">{t('dev.brand.stats.overdueDesc')}</div>
            </div>
            <div className="stat">
              <div className="stat-title">{t('dev.brand.stats.profitTitle')}</div>
              <div className="stat-value text-success">{SAMPLE_STATS.profit}</div>
              <div className="stat-desc">{t('dev.brand.stats.profitDesc')}</div>
            </div>
          </div>
          <p className="mt-3 text-xs text-base-content/60">
            <Trans i18nKey="dev.brand.stats.note" components={RICH} />
          </p>
        </section>

        {/* ---------------------------------------------------------------- card */}
        <section>
          <h2 className="mb-3 text-xl font-semibold text-base-content">{t('dev.brand.cards.title')}</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="card bg-base-100 shadow-md">
              <div className="card-body">
                <h3 className="card-title">{SAMPLE_CARD.apartment}</h3>
                <p className="text-base-content/70">
                  {t('dev.brand.cards.sampleBody', {
                    date: SAMPLE_CARD.until,
                    rent: SAMPLE_CARD.rent,
                  })}
                </p>
                <div className="card-actions justify-end">
                  <span className="chip chip-success">{t('dev.brand.chips.paid')}</span>
                  <button type="button" className="btn btn-primary btn-sm">{t('common.open')}</button>
                </div>
              </div>
            </div>
            <div className="card border border-base-300 bg-base-100">
              <div className="card-body">
                <h3 className="card-title">{t('dev.brand.cards.borderedTitle')}</h3>
                <p className="text-base-content/70">
                  <Trans i18nKey="dev.brand.cards.borderedBody" components={RICH} />
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- modal */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.modal.title')}</h2>
          <p className="mt-1 text-sm text-base-content/70">
            <Trans i18nKey="dev.brand.modal.lead" components={RICH} />
          </p>
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={() => dialogRef.current?.showModal()}
          >
            {t('dev.brand.modal.trigger')}
          </button>

          <dialog ref={dialogRef} className="modal">
            <div className="modal-box">
              <h3 className="text-lg font-bold text-base-content">
                {t('dev.brand.modal.confirmTitle')}
              </h3>
              <p className="py-4 text-base-content/70">
                <Trans i18nKey="dev.brand.modal.confirmBody" components={RICH} />
              </p>
              <div className="modal-action">
                <form method="dialog" className="flex gap-2">
                  <button type="submit" className="btn btn-ghost">{t('common.cancel')}</button>
                  <button type="submit" className="btn btn-primary">{t('common.confirm')}</button>
                </form>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop">
              <button type="submit">{t('common.close')}</button>
            </form>
          </dialog>
        </section>

        {/* ---------------------------------------------------------------- table */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.table.title')}</h2>
          <p className="mt-1 text-sm text-base-content/70">{t('dev.brand.table.lead')}</p>
          <div className="mt-4 overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>{t('dev.brand.table.apartment')}</th>
                  <th>{t('dev.brand.table.tenant')}</th>
                  <th>{t('dev.brand.table.rent')}</th>
                  <th>{t('dev.brand.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {sampleRows.map((row) => (
                  <tr key={row.apartment}>
                    <td>{row.apartment}</td>
                    <td>{row.tenant}</td>
                    <td className="font-mono">{row.rent}</td>
                    <td>
                      <span className={row.chip}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ---------------------------------------------------------------- form shim */}
        <section className="section-card">
          <h2 className="text-xl font-semibold text-base-content">{t('dev.brand.form.title')}</h2>
          <p className="mt-1 text-sm text-base-content/70">
            <Trans i18nKey="dev.brand.form.lead" components={RICH} />
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="form-control">
              <label className="label" htmlFor="brand-demo-rent">
                <span className="label-text">{t('dev.brand.form.rentLabel')}</span>
              </label>
              <input
                id="brand-demo-rent"
                type="number"
                placeholder={t('dev.brand.form.rentPlaceholder')}
                className="input input-bordered w-full"
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="brand-demo-status">
                <span className="label-text">{t('dev.brand.form.statusLabel')}</span>
              </label>
              <select
                id="brand-demo-status"
                className="select select-bordered w-full"
                defaultValue="activ"
              >
                <option value="activ">{t('dev.brand.form.statusActive')}</option>
                <option value="incheiat">{t('dev.brand.form.statusEnded')}</option>
                <option value="suspendat">{t('dev.brand.form.statusSuspended')}</option>
              </select>
            </div>
          </div>
        </section>

        <footer className="pb-6 text-sm text-base-content/60">
          <p>
            <Trans i18nKey="dev.brand.footer" components={RICH} />
          </p>
        </footer>
      </div>
    </main>
  );
}
